import {inject} from '@angular/core';
import {Router} from "@angular/router";
import {JWT} from "@app/shared/interfaces";
import {AuthService} from "@app/shared/services";
import {catchError, filter, finalize} from 'rxjs/operators';
import {httpContextDefaults} from "@app/shared/constants/http-context";
import {AppStateService} from "@app/shared/services/app-state.service";
import {BehaviorSubject, Observable, switchMap, throwError} from 'rxjs';
import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';

const enum RequestHeaderKey {
  AuthToken = 'Authorization',
  RefreshToken = 'X-REFRESH-TOKEN',
  APIKey = 'API-KEY',
  ContentType = 'Content-Type'
}

const tokenSubject = new BehaviorSubject<JWT | null>(null);
const refreshTokenInProgress = {state: false};

export const tokenInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const appStateService = inject(AppStateService);

  const params = request.context.get(httpContextDefaults);

  if (params?.skipAuth) {
    return next(request);
  }

  if (!request.url.includes('refresh-token')) {
    request = addAuthHeader(request, appStateService);
  }

  if (!request.headers.has('Content-Type') && !(request.body instanceof FormData)) {
    request = request.clone({headers: request.headers.set(RequestHeaderKey.ContentType, 'application/json')});
  }

  return next(request)
    .pipe(
      catchError((error: HttpErrorResponse) => {

        console.log('router.url', router.url);
        console.log('route match', appStateService.getLngRout('auth'));

        if (error.status === 401 && !router.url.includes(appStateService.getLngRout('auth')) && !error?.error?.refreshTokenExpired) {
          return refreshToken(request, next, authService, router, tokenSubject, refreshTokenInProgress, appStateService);
        } else {
          return throwError(() => error);
        }
      })
    );
};

const addAuthHeader = (request: HttpRequest<any>, appStateService: AppStateService) => {
  const authHeader = appStateService.authToken;

  if (authHeader) {
    return request.clone({
      setHeaders: {[RequestHeaderKey.AuthToken]: 'Bearer ' + authHeader}
    });
  }
  return request;
};

const refreshToken = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  tokenSubject: BehaviorSubject<JWT | null>,
  refreshTokenInProgress: { state: boolean },
  appStateService: AppStateService,
): Observable<HttpEvent<any>> => {

  if (!refreshTokenInProgress.state) {
    refreshTokenInProgress.state = true;
    tokenSubject.next(null);

    return authService.refreshToken()
      .pipe(
        switchMap((res: JWT) => {
          refreshTokenInProgress.state = false;
          tokenSubject.next(res);
          return next(addAuthHeader(request, appStateService));
        }),
        catchError(() => {
          const urls = [
            appStateService.getLngRout('dashboard'),
            appStateService.getLngRout('account'),
            appStateService.getLngRout('documents'),
          ];

          if (urls.some((url) => router.url.includes(url))) {
            router.navigate([appStateService.getLngRout('home')]);
          }

          return authService.signOut();
        }),
        finalize(() => refreshTokenInProgress.state = false)
      );

  } else {
    return tokenSubject
      .pipe(
        filter((res) => res != null),
        switchMap(() => next(addAuthHeader(request, appStateService)))
      );
  }
};

